import MiniSearch from 'minisearch';
import { db, DBPage } from '../db/db';
import { logger } from '../utils/logger';

// Fields stored in the search index. Kept separate from DBPage — only what we search over.
interface SearchDoc {
  url: string;     // document ID (normalized URL)
  title: string;
  urlText: string; // normalized + original URL concatenated, for searching path/domain fragments
  tags: string;    // humanTags joined with spaces
}

function toSearchDoc(page: DBPage): SearchDoc {
  return {
    url: page.url,
    title: page.title || '',
    urlText: [page.url, page.originalUrl].filter(Boolean).join(' '),
    tags: (page.humanTags || []).join(' '),
  };
}

// Split on whitespace and common URL/path delimiters so that e.g.
// "github.com/user/repo" → ["github", "com", "user", "repo"].
function tokenize(text: string): string[] {
  return text.toLowerCase().split(/[\s\-_./:?&#=+]+/).filter((t) => t.length > 0);
}

const searchIndex = new MiniSearch<SearchDoc>({
  fields: ['title', 'urlText', 'tags'],
  idField: 'url',
  tokenize,
  searchOptions: {
    // prefix: true means "git" matches "github". NOTE: mid-string matches like "hub" → "github"
    // are NOT supported. This is a behavior change from the previous String.includes() approach.
    prefix: true,
    combineWith: 'AND', // all query tokens must match (same as previous behavior)
    boost: { title: 2 }, // title matches rank higher than URL/tag matches
  },
});

let initializationPromise: Promise<void> | null = null;
let indexReady = false;

async function doInitialize(): Promise<void> {
  try {
    logger.info('[Search] Building full-text search index...');
    const pages = await db.pages.toArray();
    // addAllAsync yields between chunks to avoid blocking the event loop
    await searchIndex.addAllAsync(pages.map(toSearchDoc), { chunkSize: 500 });

    // Register incremental update hooks AFTER bulk load to avoid double-indexing pages
    // that were in the initial snapshot.
    db.pages.hook('creating', (_primKey, obj: DBPage) => {
      if (searchIndex.has(obj.url)) {
        searchIndex.replace(toSearchDoc(obj));
      } else {
        searchIndex.add(toSearchDoc(obj));
      }
    });

    // The updating hook uses Dexie's `this.onsuccess` pattern to get the post-commit
    // object. The TypeScript types don't expose the `this` context, and Dexie's
    // declared signature requires `any` for modifications — both are intentional.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    db.pages.hook('updating', function (this: any, _mods: { [keyPath: string]: any }, _primKey: string, _obj: DBPage) {
      this.onsuccess = (updatedObj: DBPage) => {
        if (updatedObj) {
          if (searchIndex.has(updatedObj.url)) {
            searchIndex.replace(toSearchDoc(updatedObj));
          } else {
            searchIndex.add(toSearchDoc(updatedObj));
          }
        }
      };
    });

    db.pages.hook('deleting', (primKey: string) => {
      searchIndex.discard(primKey); // no-op if not in index
    });

    indexReady = true;
    logger.info(`[Search] Index ready with ${pages.length} documents.`);
  } catch (e) {
    logger.error('[Search] Failed to initialize search index:', e);
  }
}

// Call once at app startup. Safe to call multiple times — only initializes once.
export function initializeSearchIndex(): Promise<void> {
  if (!initializationPromise) {
    initializationPromise = doInitialize();
  }
  return initializationPromise;
}

export function isSearchIndexReady(): boolean {
  return indexReady;
}

// Returns URLs of matching pages, sorted by relevance score (highest first).
export function searchPages(queryTokens: string[]): string[] {
  if (!indexReady || queryTokens.length === 0) return [];
  return searchIndex.search(queryTokens.join(' ')).map((r) => r.id as string);
}
